import { ref, onBeforeUnmount, nextTick } from 'vue';

/**
 * Menu kecil yang mengambang di atas halaman (pilih tema, pilih bahasa,
 * menu aksi pada baris tabel).
 *
 * MASALAH YANG DIPECAHKAN
 * Sebelumnya tiap menu memakai `position: absolute` di dalam pemicunya.
 * Cara itu rusak di tiga tempat sekaligus, dan ketiganya tampak seperti
 * "menunya kepotong":
 *
 *   1. Di dalam header. Header memakai `backdrop-filter`, dan properti itu
 *      membuat elemen menjadi containing block bagi keturunan yang
 *      `position: fixed` — sekaligus memotong apa pun yang keluar dari
 *      kotaknya.
 *   2. Di dalam footer. Menu selalu terbuka ke BAWAH, sedangkan footer
 *      berada di dasar halaman: menunya keluar layar dan tidak terjangkau.
 *   3. Di dalam tabel yang bisa digeser mendatar. `overflow` pada wadah
 *      tabel memotongnya.
 *
 * PENYELESAIAN
 * Menu dipindahkan ke <body> lewat Teleport, lalu posisinya dihitung dari
 * bounding rect pemicunya dan dipasang `position: fixed`. Tidak ada lagi
 * leluhur yang bisa memotong atau menggeser.
 *
 * Cara pakai:
 *   const { terbuka, gaya, pemicuRef, menuRef, alihkan, tutup } =
 *     useMenuMengambang();
 */
export function useMenuMengambang(pilihan = {}) {
  const { lebarMinimal = 200, jarak = 8 } = pilihan;

  const terbuka = ref(false);
  const gaya = ref({});
  const pemicuRef = ref(null);
  const menuRef = ref(null);

  function hitungPosisi() {
    const pemicu = pemicuRef.value;
    const menu = menuRef.value;
    if (!pemicu || !menu) return;

    const r = pemicu.getBoundingClientRect();
    const tinggiMenu = menu.offsetHeight;
    const lebarMenu = Math.max(menu.offsetWidth, lebarMinimal);

    /*
     * Buka ke ATAS bila ruang di bawah tidak cukup.
     * Ini yang membuat menu di footer tetap terlihat: di dasar halaman
     * ruang bawah selalu nol, dan tanpa pembalikan ini menunya tergambar
     * di luar layar.
     */
    const ruangBawah = window.innerHeight - r.bottom;
    const ruangAtas = r.top;
    const keAtas = ruangBawah < tinggiMenu + jarak && ruangAtas > ruangBawah;

    // Ditahan di dalam layar pada sisi kanan dan kiri.
    const kiri = Math.min(
      Math.max(jarak, r.right - lebarMenu),
      window.innerWidth - lebarMenu - jarak
    );

    // Bila ruang atas maupun bawah sama-sama sempit (layar pendek/mendatar),
    // menu dibatasi tingginya dan dibuat bisa digulir sendiri.
    const tinggiTersedia = (keAtas ? ruangAtas : ruangBawah) - jarak * 2;

    gaya.value = {
      position: 'fixed',
      top: keAtas ? `${Math.max(jarak, r.top - tinggiMenu - jarak)}px` : `${r.bottom + jarak}px`,
      left: `${Math.max(jarak, kiri)}px`,
      maxHeight: `${Math.max(140, tinggiTersedia)}px`,
      overflowY: 'auto',
    };
  }

  function tutup() {
    if (!terbuka.value) return;
    terbuka.value = false;
    lepas();
  }

  function klikLuar(e) {
    if (menuRef.value?.contains(e.target)) return;
    if (pemicuRef.value?.contains(e.target)) return;
    tutup();
  }

  function tekanEscape(e) {
    if (e.key === 'Escape') {
      tutup();
      pemicuRef.value?.focus?.();
    }
  }

  function pasang() {
    document.addEventListener('click', klikLuar);
    document.addEventListener('keydown', tekanEscape);
    // capture:true supaya gulir pada wadah mana pun ikut tertangkap,
    // bukan hanya gulir jendela.
    window.addEventListener('scroll', tutup, true);
    window.addEventListener('resize', tutup);
  }

  function lepas() {
    document.removeEventListener('click', klikLuar);
    document.removeEventListener('keydown', tekanEscape);
    window.removeEventListener('scroll', tutup, true);
    window.removeEventListener('resize', tutup);
  }

  function buka() {
    terbuka.value = true;
    nextTick(hitungPosisi);
    pasang();
  }

  function alihkan() {
    if (terbuka.value) tutup();
    else buka();
  }

  onBeforeUnmount(lepas);

  return { terbuka, gaya, pemicuRef, menuRef, alihkan, buka, tutup, hitungPosisi };
}

export default useMenuMengambang;
